import { ReactNode, useMemo } from "react";
import { CacheBuster } from "react-cache-refresh";
import { Spin, Typography, Flex } from "antd";

import { appVersion, isProduction, isDevelopment } from "@settings";

const { Title, Text } = Typography;

interface CacheRefreshProviderProps {
  children: ReactNode;
}

/**
 * CacheRefreshProvider wraps the application with automatic cache busting functionality.
 *
 * Features:
 * - Automatically detects new app versions by comparing with /meta.json
 * - Clears browser caches when version mismatch is detected
 * - Integrates with New Relic for monitoring cache refresh events
 * - Configurable console logging based on environment
 * - Custom loading component during version checks
 *
 * The provider uses the app version from package.json and compares it with
 * the version in /meta.json which should be updated during deployment.
 */
export const CacheRefreshProvider = ({
  children,
}: CacheRefreshProviderProps) => {
  const LoadingComponent = useMemo(
    () => (
      <Flex
        vertical
        justify="center"
        align="center"
        style={{
          height: "100vh",
          backgroundColor: "#fff",
          padding: 20,
        }}
      >
        <Spin size="large" style={{ marginBottom: 24 }} />

        <Title level={4} style={{ marginBottom: 12, textAlign: "center" }}>
          Verificando versão do sistema...
        </Title>

        <Text
          type="secondary"
          style={{
            textAlign: "center",
            maxWidth: 400,
            lineHeight: 1.5,
          }}
        >
          Estamos garantindo que você utilize a versão mais recente disponível.
        </Text>
      </Flex>
    ),
    [],
  );

  if (isDevelopment) {
    return <>{children}</>;
  }

  return (
    <CacheBuster
      currentAppVersion={appVersion}
      loadingComponent={LoadingComponent}
      hideConsoleLogs={isProduction}
      metaJsonPath="/meta.json"
    >
      {children}
    </CacheBuster>
  );
};
